import { Box, Collapse, Flex, Text } from '@chakra-ui/react'
import { DropdownOptionItem, InsuranceInfoOption, insuranceInfoOptions } from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'
import { useField } from 'react-final-form'
import { useForm } from '../../../FinalForm/FormProvider'
import { useFormWizard } from '../../../FormWizard/context'
import { useInsuranceInfoStage } from '../InsuranceInfoStageContext'
import { useInsuranceInfoStep } from '../InsuranceInfoStepContext'
// import { CoverageOptionSelectButton } from './CoverageOptionSelectButton'
import { CoverageWillChange } from './CoverageWillChange'
import { MultipleCoverageOptions } from './MultipleCoverageOptions'
import { NoCoverage } from './NoCoverage'
import { NotPregnant } from './NotPregnant'
import { OptionSelectButtons, SelectedOptionView } from './OptionSelectButton'

const SelectedCoverageOptionView = ({
  option,
  loading,
}: {
  option: InsuranceInfoOption | null | undefined
  loading?: boolean
}) => {
  const { step, setStep } = useInsuranceInfoStep()
  const { readOnly } = useFormWizard()

  return (
    <Collapse style={{ width: '100%' }} in={step !== 'option'}>
      <SelectedOptionView
        onEditClick={readOnly ? () => {} : () => setStep('option')}
        selected={option}
        options={insuranceInfoOptions}
        loading={loading}
      />
    </Collapse>
  )
}

const CoverageOptionSelectButtons = () => {
  const { readOnly } = useFormWizard()
  const { type } = useInsuranceInfoStage()
  const { change, saveWithoutValidation } = useForm()
  const { step } = useInsuranceInfoStep()
  const {
    input: { value: primaryCoverage },
  } = useField('primaryCoverage', { subscription: { value: true } })
  const [loadingOption, setLoadingOption] = useState<InsuranceInfoOption | null>(null)
  const onSelectOption = useCallback(
    async (option: InsuranceInfoOption) => {
      const existingIsMedicaid = primaryCoverage?.isMedicaid

      change('option', option)
      if (option === 'one-plan')
        change(
          'primaryCoverage',
          existingIsMedicaid === false
            ? { ...primaryCoverage, isMedicaid: false }
            : { isMedicaid: false },
        )
      if (option === 'medicaid')
        change(
          'primaryCoverage',
          existingIsMedicaid === true
            ? { ...primaryCoverage, isMedicaid: true }
            : { isMedicaid: true },
        )
      setLoadingOption(option ?? null)
      await saveWithoutValidation(false)
      setLoadingOption(null)
    },
    [change, primaryCoverage, saveWithoutValidation],
  )

  const options = useMemo<DropdownOptionItem<InsuranceInfoOption>[]>(
    () =>
      insuranceInfoOptions.reduce<DropdownOptionItem<InsuranceInfoOption>[]>((acc, option) => {
        switch (option.id) {
          case 'coverage-will-change':
          case 'not-pregnant':
            return type === 'pregnancy' ? [...acc, option] : acc
          default:
            return [...acc, option]
        }
      }, []),
    [type],
  )

  if (readOnly) return null

  return (
    <Collapse style={{ width: '100%' }} in={step === 'option'}>
      <OptionSelectButtons
        options={options}
        onSelectOption={onSelectOption}
        loadingOption={loadingOption}
      />
    </Collapse>
  )
}
export const CoverageOptionSelect = () => {
  const { insuranceInfoData, isSaving } = useInsuranceInfoStage()
  const { step } = useInsuranceInfoStep()
  const { option = null } = insuranceInfoData ?? {}
  const {
    input: { value },
  } = useField('option', { subscription: { value: true } })
  const isRefetchingOption = option !== value && isSaving

  return (
    <Flex flexFlow="column" width="100%">
      <Text px={1} fontWeight={600} color="gray.600" fontSize="lg">
        Which of these best describes your insurance coverage?
      </Text>
      <Box py={2}>
        <CoverageOptionSelectButtons />
        <SelectedCoverageOptionView option={option} loading={isRefetchingOption} />
      </Box>
      <Collapse in={step !== 'option'}>
        <CoverageWillChange />
        <MultipleCoverageOptions />
        <NoCoverage />
        <NotPregnant />
      </Collapse>
    </Flex>
  )
}
